// ─── Telegram → Avvisi ai soci ───────────────────────────────────────────────
//
// 👀 **QUESTA SCHERMATA ESISTE PER UNA COSA SOLA: far vedere un avviso che non è
// partito.** Un messaggio mancato non produce nessun errore da nessuna parte —
// chi lo aspettava pensa che sia in ritardo, chi lo ha fatto partire crede sia
// arrivato, e il guasto lo scopre la persona che si presenta a un campo che non
// è più suo. 📌 *Una funzione che tace è più difficile da riparare di una che
// si rifiuta.*
//
// 🎨 25/09/2026, dal mockup approvato (`avvisi-ai-soci-operativa-mockup.html`):
// 🗣️ *«rendiamola più operativa e facile da consultare»*. In ordine: se il bot
// passa, cosa c'è da mandare OGGI, e i non consegnati raggruppati per motivo.
// ⚖️ La riga in cima diceva «manca TELEGRAM_BOT_TOKEN», che è vero — il
// gestionale non manda da sé — ma fa credere a un guasto proprio mentre il bot
// consegna. La domanda giusta è un'altra: *il bot passa?*

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  ETICHETTE_GESTO,
  ETICHETTE_PERIODO_AVVISI,
  PERIODI_AVVISI,
  fraseDelloStato,
  spiegaMotivo,
  statoDelBot,
  type Avviso,
  type DestinatarioStaff,
  type PeriodoAvvisi,
  type QuadroAvvisi,
  type StatoAvviso,
} from '@pmo/shared';
import { chiama } from '../../lib/api.js';
import { useSessione } from '../../features/auth/sessione.js';
import { Attesa, Guasto, Vuoto } from '../../components/pezzi.js';
import { SoloLettura, usaModifica, usaRilettura } from './comune.js';

const CHIAVE = 'avvisi';
/** La pagina si rilegge da sola: un avviso «in attesa» deve farsi vedere partire. */
const OGNI = 30_000;
const ATTESA_TASTIERA = 300;

/** Le linguette. «Non da mandare» non ne ha una: si vede fra «Tutti». */
const LINGUETTE: Array<{ stato: StatoAvviso | ''; testo: string; classe: string }> = [
  { stato: '', testo: 'Tutti', classe: '' },
  { stato: 'pending', testo: 'In attesa', classe: 'in-attesa' },
  { stato: 'delivered', testo: 'Consegnati', classe: '' },
  { stato: 'failed', testo: 'Non consegnati', classe: 'non-consegnati' },
];

/** Il numero accanto alla voce del menu: i non consegnati di oggi. */
export function usaNonConsegnatiOggi(abilitato: boolean): number {
  const q = useQuery({
    queryKey: [CHIAVE, 'conto'],
    queryFn: () => chiama<{ nonConsegnatiOggi: number }>('GET', '/notices/count'),
    enabled: abilitato,
    refetchInterval: 120_000,
    staleTime: 60_000,
  });
  return q.data?.nonConsegnatiOggi ?? 0;
}

export function Avvisi(): React.JSX.Element {
  const modificabile = usaModifica('manage_settings');
  const [stato, setStato] = useState<StatoAvviso | ''>('');
  const [periodo, setPeriodo] = useState<PeriodoAvvisi>('oggi');
  const [motivo, setMotivo] = useState<string | null>(null);
  const [scritto, setScritto] = useState('');
  const [cercato, setCercato] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setCercato(scritto.trim()), ATTESA_TASTIERA);
    return () => clearTimeout(t);
  }, [scritto]);

  const quadro = useQuery({
    queryKey: [CHIAVE, 'quadro', periodo],
    queryFn: () => chiama<QuadroAvvisi>('GET', `/notices/state?periodo=${periodo}`),
    refetchInterval: OGNI,
  });
  const domanda = new URLSearchParams({ limit: '100', periodo });
  if (stato) domanda.set('status', stato);
  if (cercato) domanda.set('q', cercato);
  if (motivo) domanda.set('motivo', motivo);
  const elenco = useQuery({
    queryKey: [CHIAVE, 'elenco', domanda.toString()],
    queryFn: () => chiama<{ items: Avviso[]; nextCursor: string | null }>('GET', `/notices?${domanda}`),
    placeholderData: (prima) => prima,
    refetchInterval: OGNI,
  });

  const q = quadro.data;
  const conti: Partial<Record<StatoAvviso, number>> = q?.perStato ?? {};
  const tutti = Object.values(conti).reduce<number>((a, n) => a + (n ?? 0), 0);
  const items = elenco.data?.items ?? [];
  const scegli = (s: StatoAvviso | '') => {
    setStato(s);
    // Un motivo è un pezzo dei non consegnati: fuori di lì non vuol dire niente.
    if (s !== 'failed') setMotivo(null);
  };

  return (
    <section className="pagina-avvisi">
      <header className="testata">
        <h2>Avvisi ai soci</h2>
        <p className="tenue">
          I messaggi che il circolo manda ai soci su Telegram: risposte della segreteria, partite spostate,
          annullate, giocatori entrati o usciti.
        </p>
        <SoloLettura mostra={modificabile} />
      </header>

      {q && <StatoDelBot quadro={q} />}

      <div className="comandi-avvisi">
        <div className="linguette-avvisi" role="tablist">
          {LINGUETTE.map((l) => (
            <button
              key={l.testo}
              type="button"
              role="tab"
              aria-selected={stato === l.stato}
              className={`linguetta ${l.classe} ${stato === l.stato ? 'scelta' : ''}`}
              onClick={() => scegli(l.stato)}
            >
              {l.testo} <b>{l.stato ? (conti[l.stato] ?? 0) : tutti}</b>
            </button>
          ))}
        </div>
        <select
          aria-label="Periodo"
          value={periodo}
          onChange={(e) => setPeriodo(e.target.value as PeriodoAvvisi)}
        >
          {PERIODI_AVVISI.map((p) => (
            <option key={p} value={p}>
              {ETICHETTE_PERIODO_AVVISI[p]}
            </option>
          ))}
        </select>
        <input
          type="search"
          className="cerca-avvisi"
          placeholder="Cerca un socio…"
          aria-label="Cerca un socio"
          value={scritto}
          onChange={(e) => setScritto(e.target.value)}
        />
      </div>

      {stato === 'failed' && q && q.motivi.length > 0 && !motivo && (
        <div className="motivi-avvisi">
          {q.motivi.map((m) => (
            <div key={m.motivo} className="motivo-avvisi">
              <span className="n">{m.quante}</span>
              <span className="titolo">{m.motivo}</span>
              {spiegaMotivo(m.motivo) && <span className="spiega">{spiegaMotivo(m.motivo)}</span>}
              <button type="button" className="contorno" onClick={() => setMotivo(m.motivo)}>
                Vedi i soci
              </button>
            </div>
          ))}
        </div>
      )}
      {motivo && (
        <p className="solo-motivo">
          Solo: <strong>{motivo}</strong>{' '}
          <button type="button" className="piatto" onClick={() => setMotivo(null)}>
            togli il filtro
          </button>
        </p>
      )}

      {elenco.isLoading ? (
        <Attesa cosa="gli avvisi" />
      ) : elenco.error ? (
        <Guasto errore={elenco.error} />
      ) : items.length === 0 ? (
        <Vuoto
          titolo="Niente da vedere"
          perché={
            cercato
              ? `Nessun avviso per «${cercato}» in questo periodo.`
              : 'In questo periodo nessun gesto della segreteria aveva qualcosa da dire a un socio.'
          }
        />
      ) : (
        <div className="elenco-avvisi">
          {items.map((a) => (
            <SchedaAvviso key={a.id} avviso={a} modificabile={modificabile} />
          ))}
          {elenco.data?.nextCursor && (
            <p className="tenue piccolo">
              Qui si vedono i 100 più recenti: per gli altri scegli un periodo più corto o cerca un socio.
            </p>
          )}
        </div>
      )}

      <Destinatari modificabile={modificabile} />
    </section>
  );
}

function StatoDelBot({ quadro }: { quadro: QuadroAvvisi }): React.JSX.Element {
  const s = statoDelBot(quadro, new Date());
  return (
    <p className={`stato-del-bot tono-${s.tono}`} role="status">
      <span className="pallino" aria-hidden="true" />
      <span>
        <strong>{s.titolo}</strong> {s.dettaglio}
      </span>
    </p>
  );
}

/** Oltre questa misura il messaggio si vede accorciato, con «Mostra tutto». */
function èLungo(testo: string): boolean {
  return testo.length > 220 || testo.split('\n').length > 6;
}

function SchedaAvviso({
  avviso,
  modificabile,
}: {
  avviso: Avviso;
  modificabile: boolean;
}): React.JSX.Element {
  const { profilo } = useSessione();
  const rileggi = usaRilettura(CHIAVE);
  const [tutto, setTutto] = useState(false);
  const riprova = useMutation({
    meta: { fatto: 'avviso rimesso in coda: riparte al prossimo giro' },
    mutationFn: () => chiama('POST', `/notices/${avviso.id}/retry`),
    onSuccess: rileggi,
  });

  const testo = avviso.payload.testo;
  const lungo = èLungo(testo);
  const { stato, perche } = fraseDelloStato(avviso);
  const vedeSoci = profilo?.permissions.includes('view_members') ?? false;
  const chiuso = avviso.status === 'failed' || avviso.status === 'skipped';

  return (
    <article className={`scheda-avviso ${avviso.status}`}>
      <div className="testa">
        <span className="chi">{avviso.memberName}</span>
        <span className="cosa">{ETICHETTE_GESTO[avviso.gesture]}</span>
        <span className="quando">{quando(avviso.createdAt)}</span>
      </div>
      {/* 📌 La frase è quella VERA, quella che è partita o che partirà: non
          una ricostruzione fatta qui. Mostrarne un'altra vorrebbe dire far
          controllare una cosa diversa da quella consegnata. */}
      <div className={`bolla${lungo && !tutto ? ' corta' : ''}`}>{testo}</div>
      {lungo && (
        <button type="button" className="piatto" onClick={() => setTutto(!tutto)}>
          {tutto ? 'Mostra meno' : 'Mostra tutto il messaggio'}
        </button>
      )}
      <div className="esito-avviso">
        <span className="stato">{stato}</span>
        {perche && <span className="perche">{perche}</span>}
        {chiuso && (vedeSoci || modificabile) && (
          <span className="bottoni">
            {vedeSoci && (
              <Link className="bottone-contorno" to={`/soci?socio=${avviso.memberId}`}>
                Apri la scheda
              </Link>
            )}
            {modificabile && (
              <button type="button" disabled={riprova.isPending} onClick={() => riprova.mutate()}>
                {riprova.isPending ? 'Un attimo…' : 'Riprova'}
              </button>
            )}
          </span>
        )}
      </div>
      <Guasto errore={riprova.error} />
    </article>
  );
}

/** «oggi 16:03», «ieri 12:22», oppure «23/09 10:57»: all'ora del circolo. */
function quando(iso: string): string {
  const giorno = (d: Date) => d.toLocaleDateString('it-IT', { timeZone: 'Europe/Rome' });
  const d = new Date(iso);
  const ora = d.toLocaleTimeString('it-IT', { timeZone: 'Europe/Rome', hour: '2-digit', minute: '2-digit' });
  const oggi = new Date();
  if (giorno(d) === giorno(oggi)) return `oggi ${ora}`;
  if (giorno(d) === giorno(new Date(oggi.getTime() - 86_400_000))) return `ieri ${ora}`;
  const breve = d.toLocaleDateString('it-IT', { timeZone: 'Europe/Rome', day: '2-digit', month: '2-digit' });
  return `${breve} ${ora}`;
}

/**
 * Chi, fra lo staff, riceve le notifiche di servizio.
 *
 * ⚖️ Sostituisce `pmoStaffNotificationRecipientsV1`, che oggi vive nel
 * `localStorage` di un browser: cioè su un computer solo, e sparisce con la
 * cronologia. Una lista di destinatari tenuta lì non è una configurazione del
 * circolo — è una preferenza di chi si è seduto a quella scrivania.
 */
function Destinatari({ modificabile }: { modificabile: boolean }): React.JSX.Element {
  const rileggi = usaRilettura('destinatari-staff');
  const elenco = useQuery({
    queryKey: ['destinatari-staff'],
    queryFn: () => chiama<{ items: DestinatarioStaff[] }>('GET', '/settings/notification-recipients'),
  });
  const [bozza, setBozza] = useState<Record<string, { chatId: string; active: boolean }>>({});

  const salva = useMutation({
    meta: { fatto: 'destinatari degli avvisi salvati' },
    mutationFn: (items: Array<{ userId: string; chatId: string | null; active: boolean }>) =>
      chiama('PUT', '/settings/notification-recipients', { items }),
    onSuccess: () => {
      setBozza({});
      rileggi();
    },
  });

  const items = elenco.data?.items ?? [];
  const valore = (d: DestinatarioStaff) => bozza[d.userId] ?? { chatId: d.chatId ?? '', active: d.active };

  return (
    <div className="riquadro">
      <h3>Notifiche di servizio allo staff</h3>
      <p className="tenue piccolo">
        Il codice della chat Telegram di chi, in segreteria, deve ricevere gli avvisi di servizio. È un
        numero: lo dice il bot a chi gli scrive.
      </p>
      {elenco.error && <Guasto errore={elenco.error} />}
      <div className="tabella-scorre">
        <table>
          <thead>
            <tr>
              <th>Chi</th>
              <th>Chat Telegram</th>
              <th>Riceve</th>
            </tr>
          </thead>
          <tbody>
            {items.map((d) => (
              <tr key={d.userId}>
                <td>
                  {d.userName}
                  <div className="tenue piccolo">{d.email}</div>
                </td>
                <td>
                  <input
                    value={valore(d).chatId}
                    disabled={!modificabile}
                    inputMode="numeric"
                    placeholder="1256773674"
                    onChange={(e) =>
                      setBozza({ ...bozza, [d.userId]: { ...valore(d), chatId: e.target.value } })
                    }
                  />
                </td>
                <td>
                  <input
                    type="checkbox"
                    checked={valore(d).active}
                    disabled={!modificabile}
                    onChange={(e) =>
                      setBozza({ ...bozza, [d.userId]: { ...valore(d), active: e.target.checked } })
                    }
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Guasto errore={salva.error} />
      {modificabile && (
        <button
          type="button"
          disabled={salva.isPending || Object.keys(bozza).length === 0}
          onClick={() =>
            salva.mutate(
              Object.entries(bozza).map(([userId, v]) => ({
                userId,
                // Una casella svuotata vuol dire «non ha una chat», non «stringa vuota».
                chatId: v.chatId.trim() === '' ? null : v.chatId.trim(),
                active: v.active,
              })),
            )
          }
        >
          {salva.isPending ? 'Un attimo…' : 'Salva i destinatari'}
        </button>
      )}
    </div>
  );
}
